/**
 * Sysadmin Tenant Detail — Asset Assignments tab (Wave 3 §10.2 #2 / spec §4.4).
 *
 * ⭐ Closes the founder-reported bug: per-tenant control plane for
 * which models / MCP / workflows / agents / knowledge a tenant has
 * access to.
 *
 * Wave 3 §10.2 #3a: Models full editor.
 *   Left pane:  unassigned brands × eligible engines from the catalog
 *   Right pane: currently-assigned brands with toggle / revoke
 *
 * R5 §4.4 (Round-5 fix, 2026-05-13): MCP sub-tab promoted from "Wave 4
 * placeholder" to v1 editor. Backend endpoints already exist
 * (`POST /admin/platform/mcp/tenant-assignments` etc.); this PR wires
 * the UI. Same 2-pane shape as Models.
 *
 * Workflows / Agents / Knowledge remain v2 RAW/INHERIT affordances —
 * tenants pick those up via Studio (RAW) or universal-inherit
 * (Knowledge), so no assignment editor surface is needed here.
 */

import { useEffect, useState } from 'react';
import { createFileRoute, useParams } from '@tanstack/react-router';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Construction, Cpu, Network, Workflow, Bot, BookOpen, Plus, Trash2, AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import { useArgusAssignments } from '@/lib/use-argus-assignments';
import {
  useArgusModels,
  useArgusModelDetail,
  useCreateAssignment,
  useDeleteAssignment,
  useUpdateAssignment,
  type ArgusModelSummary,
  type ArgusModelEngine,
} from '@/lib/use-argus-models';
// R5 §4.4 — MCP catalog + tenant-MCP assignment hooks.
import {
  useMcpCatalog,
  useTenantMcpAssignments,
  useCreateMcpAssignment,
  useRevokeMcpAssignment,
} from '@/lib/use-tenants';
// EP-1 §8.4 — preflight verdict + risk-accept hooks for the in-dialog banner.
import {
  usePreflightAssignment,
  type PreflightResponse,
  type PreflightVerdict,
} from '@/lib/use-sysadmin-ep1';
import { apiClient } from '@/lib/api-client';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Search, CheckCircle2, AlertCircle, ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/sysadmin/tenants/$tenantId/assignments')({
  component: TenantAssignmentsTab,
});

const SUB_TABS = [
  { key: 'models',     label: 'Models',     icon: Cpu,     status: 'v1' },
  { key: 'mcp',        label: 'MCP',        icon: Network, status: 'v1' },
  { key: 'workflows',  label: 'Workflows',  icon: Workflow, status: 'v2' },
  { key: 'agents',     label: 'Agents',     icon: Bot,     status: 'v2' },
  { key: 'knowledge',  label: 'Knowledge',  icon: BookOpen, status: 'v2' },
] as const;

type SubTabKey = typeof SUB_TABS[number]['key'];

function TenantAssignmentsTab() {
  const { tenantId } = useParams({ from: '/sysadmin/tenants/$tenantId/assignments' });
  const [sub, setSub] = useState<SubTabKey>('models');

  return (
    <div className="space-y-4">
      {/*
        Issue #956: every tenant detail tab must render its own h1 so
        the a11y landmark + page selector survive direct reload. The
        parent layout h1 is "{tenant.name}" — the tab-level h1 here
        names the section (Asset Assignments per spec §4.4) the
        operator is on.
      */}
      <h1 className="text-xl font-semibold tracking-tight">Asset Assignments</h1>
      <div className="border-b">
        <nav className="flex items-center gap-0.5 overflow-x-auto">
          {SUB_TABS.map((t) => {
            const Icon = t.icon;
            const active = sub === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setSub(t.key)}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                  active
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                <Icon className="h-4 w-4" />
                {t.label}
                {t.status === 'v2' && (
                  <Badge variant="secondary" className="ml-1 text-[10px]">v2</Badge>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {sub === 'models' && <ModelsAssignmentEditor tenantId={tenantId} />}
      {sub === 'mcp' && <McpAssignmentEditor tenantId={tenantId} />}
      {(sub === 'workflows' || sub === 'agents' || sub === 'knowledge') && (
        <WaveFourPlaceholder kind={sub} />
      )}
    </div>
  );
}

// ─── v1: Models 2-pane editor ────────────────────────────────────────────────

function ModelsAssignmentEditor({ tenantId }: { tenantId: string }) {
  const assignments = useArgusAssignments(tenantId);
  const catalog = useArgusModels();
  const updateAssignment = useUpdateAssignment();
  const deleteAssignment = useDeleteAssignment();

  const [revokeTarget, setRevokeTarget] = useState<{ id: string; brandId: string } | null>(null);

  const assignedBrandIds = new Set(
    (assignments.data?.items ?? []).map((a) => a.argusModelId),
  );
  const unassignedBrands = (catalog.data?.items ?? []).filter(
    (b) => b.isActive && !assignedBrandIds.has(b.id),
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
      {/* Left pane: catalog (unassigned brands) */}
      <Card className="lg:col-span-2">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Available brands
          </CardTitle>
          <CardDescription>
            Brands not yet assigned to this tenant. Click <strong>Assign</strong>
            {' '}to grant access; provenance gating applies.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-0">
          {catalog.isLoading || assignments.isLoading ? (
            <div className="px-6 py-8 text-sm text-muted-foreground animate-pulse">
              Loading catalog…
            </div>
          ) : unassignedBrands.length === 0 ? (
            <div className="px-6 py-8 text-sm text-muted-foreground text-center">
              All active brands are already assigned.
            </div>
          ) : (
            <ul className="divide-y border-y">
              {unassignedBrands.map((brand) => (
                <UnassignedBrandRow
                  key={brand.id}
                  brand={brand}
                  tenantId={tenantId}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Right pane: assigned brands */}
      <Card className="lg:col-span-3">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Assigned to this tenant</CardTitle>
          <CardDescription>
            Brands granted to this tenant. Toggle to enable/disable; click
            the trash icon to revoke access.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-0">
          {assignments.isLoading ? (
            <div className="px-6 py-8 text-sm text-muted-foreground animate-pulse">
              Loading assignments…
            </div>
          ) : assignments.error ? (
            <div className="px-6 py-8 text-sm text-destructive flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>Failed to load: {assignments.error.message}</div>
            </div>
          ) : !assignments.data || assignments.data.items.length === 0 ? (
            <div className="px-6 py-8 text-sm text-muted-foreground text-center">
              No brands assigned to this tenant yet. Assign one from the
              left pane.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Brand</TableHead>
                  <TableHead>Engine</TableHead>
                  <TableHead>Provenance</TableHead>
                  <TableHead>Enabled</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assignments.data.items.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium">{row.brandId}</TableCell>
                    <TableCell className="text-xs">
                      <span className="font-mono">{row.engineRoleLabel ?? '—'}</span>
                      <span className="text-muted-foreground ml-1">
                        ({row.engineBaseLlmKey ?? '—'})
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {row.engineProvenance ?? '—'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={row.isEnabled}
                        disabled={updateAssignment.isPending}
                        onCheckedChange={async (checked) => {
                          try {
                            await updateAssignment.mutateAsync({
                              id: row.id,
                              isEnabled: checked,
                            });
                            toast.success(
                              checked ? 'Brand enabled for tenant' : 'Brand disabled for tenant',
                            );
                          } catch (e) {
                            toast.error('Toggle failed', {
                              description: e instanceof Error ? e.message : 'Unknown error',
                            });
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Revoke"
                        className="text-destructive hover:bg-destructive/10 h-8 w-8 p-0"
                        onClick={() => setRevokeTarget({ id: row.id, brandId: row.brandId })}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={revokeTarget !== null}
        onOpenChange={(open) => !open && setRevokeTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke brand access</AlertDialogTitle>
            <AlertDialogDescription>
              Tenant will lose access to brand
              {' '}<code className="bg-muted px-1 rounded">{revokeTarget?.brandId}</code>
              {' '}immediately. In-flight requests routed to that brand will fail
              until reassigned. This is reversible (re-assign from the
              catalog) but pending tenant chat sessions will see errors
              until they switch brands.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                if (!revokeTarget) return;
                try {
                  await deleteAssignment.mutateAsync({ id: revokeTarget.id });
                  toast.success(`Revoked ${revokeTarget.brandId}`);
                } catch (e) {
                  toast.error('Revoke failed', {
                    description: e instanceof Error ? e.message : 'Unknown error',
                  });
                }
                setRevokeTarget(null);
              }}
            >
              Revoke access
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Unassigned brand row with engine selector + assign button ──────────────

function UnassignedBrandRow({
  brand,
  tenantId,
}: {
  brand: ArgusModelSummary;
  tenantId: string;
}) {
  const detail = useArgusModelDetail(brand.id);
  const [selectedEngineId, setSelectedEngineId] = useState<string | null>(null);
  const [assignOpen, setAssignOpen] = useState(false);

  const engines = (detail.data?.engines ?? []).filter((e) => e.isActive);
  const defaultEngine =
    engines.find((e) => e.isDefault) ?? engines[0] ?? null;
  const effectiveEngineId = selectedEngineId ?? defaultEngine?.id ?? null;

  return (
    <li className="px-4 py-3 flex items-start gap-3">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{brand.displayName}</div>
        <div className="text-xs text-muted-foreground line-clamp-1">
          {brand.description}
        </div>
        <div className="flex items-center gap-1.5 mt-1.5">
          <Badge variant="outline" className="text-[10px]">{brand.modality}</Badge>
          <Badge variant="secondary" className="text-[10px]">
            {brand.engineCount} engine{brand.engineCount === 1 ? '' : 's'}
          </Badge>
          {brand.factoryDefault && (
            <Badge className="text-[10px]">factory</Badge>
          )}
        </div>

        {detail.isLoading ? (
          <div className="text-xs text-muted-foreground mt-2 animate-pulse">
            Loading engines…
          </div>
        ) : engines.length === 0 ? (
          <div className="text-xs text-amber-600 mt-2 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" />
            No active engines — cannot assign.
          </div>
        ) : (
          <div className="mt-2 flex items-center gap-2">
            <Select
              value={effectiveEngineId ?? undefined}
              onValueChange={(v) => setSelectedEngineId(v)}
            >
              <SelectTrigger className="h-8 w-44 text-xs">
                <SelectValue placeholder="Select engine" />
              </SelectTrigger>
              <SelectContent>
                {engines.map((e: ArgusModelEngine) => (
                  <SelectItem key={e.id} value={e.id} className="text-xs">
                    {e.roleLabel}
                    {e.isDefault && (
                      <span className="text-muted-foreground"> (default)</span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              disabled={!effectiveEngineId}
              onClick={() => setAssignOpen(true)}
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              Assign
            </Button>
          </div>
        )}

        {effectiveEngineId && (
          <AssignWithPreflightDialog
            open={assignOpen}
            onOpenChange={setAssignOpen}
            tenantId={tenantId}
            brand={brand}
            engineId={effectiveEngineId}
          />
        )}
      </div>
    </li>
  );
}

// ─── EP-1 §8.4 — Preflight banner dialog ──────────────────────────────────
//
// Flow per spec:
//   1. Dialog opens → preflight fires automatically (POST /preflight-assignment)
//   2. Verdict surfaces inline ABOVE the Save button:
//      GREEN  → Save enabled, no banner
//      YELLOW → Save enabled, yellow advisory banner with reasoning
//      RED    → Save disabled until operator types "RISK ACCEPT" + reason
//               + helper-budget acknowledgment in ms (typed-confirm pattern
//               per spec §8.4)
//   3. Save → POST /tenant-argus-model-assignments with isEnabled=true.
//             If RED + risk_acceptance present, the API accepts the write.
function AssignWithPreflightDialog({
  open,
  onOpenChange,
  tenantId,
  brand,
  engineId,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  tenantId: string;
  brand: ArgusModelSummary;
  engineId: string;
}) {
  const create = useCreateAssignment();
  const preflight = usePreflightAssignment(tenantId);

  const [verdict, setVerdict] = useState<PreflightResponse | null>(null);
  const [preflightError, setPreflightError] = useState<string | null>(null);

  // Risk-accept inputs (only used when verdict === 'red')
  const [typedConfirm, setTypedConfirm] = useState('');
  const [riskReason, setRiskReason] = useState('');
  const [budgetAck, setBudgetAck] = useState('');

  // Fire preflight on open.
  useEffect(() => {
    if (!open) return;
    setVerdict(null);
    setPreflightError(null);
    setTypedConfirm('');
    setRiskReason('');
    setBudgetAck('');
    (async () => {
      try {
        const res = await preflight.mutateAsync({ brandId: brand.id });
        setVerdict(res);
      } catch (e) {
        setPreflightError(e instanceof Error ? e.message : 'Unknown error');
      }
    })();
    // preflight is stable-by-ref — intentionally not listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, brand.id, tenantId]);

  const isRed = verdict?.verdict === 'red';
  const expectedBudget = verdict?.measuredHelperBudgetMs ?? 0;

  const typedConfirmCorrect = typedConfirm === 'RISK ACCEPT';
  const reasonValid = riskReason.trim().length >= 20;
  const budgetAckCorrect = Number(budgetAck) === expectedBudget && budgetAck !== '';
  const riskAcceptComplete =
    typedConfirmCorrect && reasonValid && budgetAckCorrect;

  // Save enabled when:
  //   - verdict loaded (not pending, not errored)
  //   - GREEN or YELLOW: always
  //   - RED: only when risk-accept fields are all valid
  const canSave =
    verdict !== null &&
    !preflight.isPending &&
    !create.isPending &&
    (!isRed || riskAcceptComplete);

  const submit = async () => {
    try {
      if (isRed) {
        // 2-step flow for RED verdict:
        //   1. Create the assignment with isEnabled=false (which
        //      bypasses preflight per argus-models.ts:2776 — preflight
        //      only fires when isEnabled is true).
        //   2. POST .../risk-accept which flips to isEnabled=true and
        //      records the override fields on the row.
        // The risk-accept route reads `req.user.id` from the JWT so
        // the operator never has to send their own UUID.
        const created = await create.mutateAsync({
          organizationId: tenantId,
          argusModelId: brand.id,
          argusModelEngineId: engineId,
          isEnabled: false,
        });
        if (created.alreadyExists) {
          toast.info(`${brand.displayName} already assigned`);
          onOpenChange(false);
          return;
        }
        await apiClient.post(
          `/admin/platform/tenants/${tenantId}/argus-models/${created.id}/risk-accept`,
          {
            reason: riskReason.trim(),
            helperBudgetAcknowledgedMs: Number(budgetAck),
          },
        );
        toast.warning(`Assigned ${brand.displayName} (risk-accepted)`, {
          description: 'The override is recorded in the platform audit chain.',
        });
        onOpenChange(false);
        return;
      }

      // GREEN/YELLOW happy path — single-step create with isEnabled=true.
      const res = await create.mutateAsync({
        organizationId: tenantId,
        argusModelId: brand.id,
        argusModelEngineId: engineId,
        isEnabled: true,
      });
      if (res.alreadyExists) {
        toast.info(`${brand.displayName} already assigned`);
      } else {
        toast.success(`Assigned ${brand.displayName}`);
      }
      onOpenChange(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Unknown error';
      if (msg.includes('PROVENANCE_VIOLATION')) {
        toast.error('Provenance violation', {
          description:
            'This engine is incompatible with the tenant compliance floor. Pick a different engine or relax the floor in Compliance.',
        });
      } else if (msg.includes('PREFLIGHT_RED')) {
        toast.error('Preflight blocked', {
          description:
            'The assignment is RED. Complete the risk-accept fields below and try again.',
        });
      } else {
        toast.error('Assign failed', { description: msg });
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Assign {brand.displayName}</DialogTitle>
          <DialogDescription>
            Preflight checks whether this brand can meet helper-budget SLOs
            on the tenant's hardware before the assignment is enabled.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          {/* Brand summary */}
          <div className="rounded border p-3 text-xs">
            <div className="font-semibold">{brand.displayName}</div>
            <div className="text-muted-foreground font-mono">{brand.brandId}</div>
            <div className="text-muted-foreground mt-1">{brand.description}</div>
          </div>

          {/* Verdict banner */}
          {preflight.isPending && !verdict && (
            <Alert>
              <RefreshCw className="h-4 w-4 animate-spin" />
              <AlertTitle>Running preflight…</AlertTitle>
              <AlertDescription>
                Checking SLO baseline for this brand on the tenant's current
                hardware profile.
              </AlertDescription>
            </Alert>
          )}

          {preflightError && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Preflight error</AlertTitle>
              <AlertDescription>{preflightError}</AlertDescription>
            </Alert>
          )}

          {verdict && <PreflightVerdictBanner verdict={verdict} />}

          {/* Risk-accept fields (only when verdict === 'red') */}
          {isRed && (
            <div className="space-y-3 rounded border border-red-300 bg-red-50/30 dark:bg-red-950/10 p-3">
              <div className="flex items-start gap-2 text-sm">
                <ShieldAlert className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold text-red-700 dark:text-red-300">
                    Override required
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    This assignment is expected to time out under normal use.
                    To proceed, complete all three fields below. The override
                    is recorded in the platform audit chain.
                  </div>
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="risk-typed" className="text-xs">
                  Type{' '}
                  <code className="bg-background px-1 rounded font-mono">
                    RISK ACCEPT
                  </code>{' '}
                  verbatim
                </Label>
                <Input
                  id="risk-typed"
                  value={typedConfirm}
                  onChange={(e) => setTypedConfirm(e.target.value)}
                  placeholder="RISK ACCEPT"
                  autoComplete="off"
                  spellCheck={false}
                  className={cn(
                    'font-mono text-sm',
                    typedConfirm && !typedConfirmCorrect && 'border-amber-400',
                    typedConfirmCorrect && 'border-emerald-400',
                  )}
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="risk-budget" className="text-xs">
                  Acknowledge helper-budget — type {expectedBudget} ms verbatim
                </Label>
                <Input
                  id="risk-budget"
                  value={budgetAck}
                  onChange={(e) => setBudgetAck(e.target.value)}
                  placeholder={String(expectedBudget)}
                  inputMode="numeric"
                  autoComplete="off"
                  spellCheck={false}
                  className={cn(
                    'font-mono text-sm',
                    budgetAck && !budgetAckCorrect && 'border-amber-400',
                    budgetAckCorrect && 'border-emerald-400',
                  )}
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="risk-reason" className="text-xs">
                  Reason (≥20 chars, audited)
                </Label>
                <Textarea
                  id="risk-reason"
                  value={riskReason}
                  onChange={(e) => setRiskReason(e.target.value)}
                  rows={2}
                  placeholder="e.g. test tenant, expected slow performance; will swap to argus_fast for production"
                  className={cn(
                    riskReason && !reasonValid && 'border-amber-400',
                    reasonValid && 'border-emerald-400',
                  )}
                />
                <div className="text-[10px] text-muted-foreground tabular-nums">
                  {riskReason.trim().length} / 20 chars minimum
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!canSave}
            onClick={submit}
            variant={isRed ? 'destructive' : 'default'}
          >
            {create.isPending
              ? 'Assigning…'
              : isRed
                ? 'Assign with override'
                : 'Assign'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PreflightVerdictBanner({
  verdict,
}: {
  verdict: PreflightResponse;
}) {
  const v: PreflightVerdict = verdict.verdict;
  if (v === 'green') {
    return (
      <Alert className="border-emerald-300 bg-emerald-50/50 dark:bg-emerald-950/10">
        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
        <AlertTitle className="text-emerald-700 dark:text-emerald-300">
          GREEN — compatibility confirmed
        </AlertTitle>
        <AlertDescription className="text-xs">
          First-token p95 ≈ {verdict.projectedFirstTokenP95Ms ?? '—'} ms is
          comfortably under the helper budget of{' '}
          {verdict.measuredHelperBudgetMs} ms on this tenant's hardware.
        </AlertDescription>
      </Alert>
    );
  }
  if (v === 'yellow') {
    return (
      <Alert className="border-amber-300 bg-amber-50/50 dark:bg-amber-950/10">
        <AlertCircle className="h-4 w-4 text-amber-600" />
        <AlertTitle className="text-amber-700 dark:text-amber-300">
          YELLOW — at risk under load
        </AlertTitle>
        <AlertDescription className="text-xs space-y-1">
          <div>
            First-token p95 ≈ {verdict.projectedFirstTokenP95Ms ?? '—'} ms
            against budget {verdict.measuredHelperBudgetMs} ms.
          </div>
          {verdict.reasoning.length > 0 && (
            <ul className="list-disc ml-4">
              {verdict.reasoning.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          )}
        </AlertDescription>
      </Alert>
    );
  }
  return (
    <Alert variant="destructive">
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle>RED — expected to time out</AlertTitle>
      <AlertDescription className="text-xs space-y-1">
        <div>
          First-token p95 ≈ {verdict.projectedFirstTokenP95Ms ?? '—'} ms
          exceeds the helper budget of {verdict.measuredHelperBudgetMs} ms by
          more than 50%.
        </div>
        {verdict.reasoning.length > 0 && (
          <ul className="list-disc ml-4">
            {verdict.reasoning.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        )}
      </AlertDescription>
    </Alert>
  );
}

/**
 * EP-1 §8.4 typed-confirm gate. Exported for unit tests so the gating
 * logic is verifiable without rendering the full dialog. Returns true
 * only when ALL three risk-accept inputs are valid.
 *
 * Pure function — no React; safe to import from .test.ts files.
 */
export function isRiskAcceptComplete(opts: {
  typedConfirm: string;
  riskReason: string;
  budgetAck: string;
  expectedBudgetMs: number;
}): boolean {
  if (opts.typedConfirm !== 'RISK ACCEPT') return false;
  if (opts.riskReason.trim().length < 20) return false;
  if (opts.budgetAck === '') return false;
  if (Number(opts.budgetAck) !== opts.expectedBudgetMs) return false;
  return true;
}

// ─── v1: MCP 2-pane editor (R5 §4.4) ─────────────────────────────────────────

function McpAssignmentEditor({ tenantId }: { tenantId: string }) {
  // Catalog (left pane) — only published rows; the trust-level badge
  // tags experimental/community servers so a sysadmin doesn't
  // assign an unvetted server to a tenant by accident.
  const [search, setSearch] = useState('');
  const catalog = useMcpCatalog({ publishedOnly: true, search: search || undefined });
  const assignments = useTenantMcpAssignments(tenantId);
  const create = useCreateMcpAssignment();
  const revoke = useRevokeMcpAssignment();

  const [revokeTarget, setRevokeTarget] = useState<{
    id: string; slug: string; displayName: string;
  } | null>(null);

  const assignedServerIds = new Set(
    (assignments.data?.items ?? []).map((a) => a.systemServerId),
  );
  const unassigned = (catalog.data?.items ?? []).filter(
    (row) => !row.retiredAt && !assignedServerIds.has(row.id),
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
      {/* Left pane: catalog (unassigned servers) */}
      <Card className="lg:col-span-2">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Available MCP servers
          </CardTitle>
          <CardDescription>
            Tier-2 vetted catalog → assign to this tenant. Tier-3 (Forge)
            servers carry an extra audit notice.
          </CardDescription>
          <div className="relative mt-2">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input
              placeholder="Search catalog…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-8 text-xs"
            />
          </div>
        </CardHeader>
        <CardContent className="px-0">
          {catalog.isLoading || assignments.isLoading ? (
            <div className="px-6 py-8 text-sm text-muted-foreground animate-pulse">
              Loading catalog…
            </div>
          ) : unassigned.length === 0 ? (
            <div className="px-6 py-8 text-sm text-muted-foreground text-center">
              {search
                ? 'No catalog rows match the search.'
                : 'All published MCP servers are already assigned.'}
            </div>
          ) : (
            <ul className="divide-y border-y max-h-[600px] overflow-y-auto">
              {unassigned.map((row) => (
                <li key={row.id} className="px-4 py-3 flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{row.displayName}</div>
                    <div className="text-xs text-muted-foreground line-clamp-2">
                      {row.description}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                      <Badge variant="outline" className="text-[10px] font-mono">
                        {row.slug}
                      </Badge>
                      <Badge
                        className={cn(
                          'text-[10px]',
                          row.trustLevel === 'official' && 'bg-emerald-600/10 text-emerald-700 border-emerald-600/30',
                          row.trustLevel === 'verified' && 'bg-blue-600/10 text-blue-700 border-blue-600/30',
                          row.trustLevel === 'community' && 'bg-amber-600/10 text-amber-700 border-amber-600/30',
                          row.trustLevel === 'experimental' && 'bg-red-600/10 text-red-700 border-red-600/30',
                        )}
                        variant="outline"
                      >
                        {row.trustLevel}
                      </Badge>
                      <Badge variant="secondary" className="text-[10px]">
                        v{row.version}
                      </Badge>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    disabled={create.isPending}
                    onClick={async () => {
                      try {
                        const res = await create.mutateAsync({
                          organizationId: tenantId,
                          systemServerId: row.id,
                        });
                        if (res.alreadyExists) {
                          toast.info(`${row.displayName} already assigned`);
                        } else {
                          toast.success(`Assigned ${row.displayName}`);
                        }
                      } catch (e) {
                        const msg = e instanceof Error ? e.message : 'Unknown error';
                        if (msg.includes('CATALOG_RETIRED')) {
                          toast.error('Server retired', {
                            description: 'This catalog row is retired and no longer accepting new assignments.',
                          });
                        } else {
                          toast.error('Assign failed', { description: msg });
                        }
                      }
                    }}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    Assign
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Right pane: assigned to this tenant */}
      <Card className="lg:col-span-3">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Assigned to this tenant</CardTitle>
          <CardDescription>
            Active MCP server installs. Click the trash icon to revoke
            (soft-delete; the assignment row stays for audit).
          </CardDescription>
        </CardHeader>
        <CardContent className="px-0">
          {assignments.isLoading ? (
            <div className="px-6 py-8 text-sm text-muted-foreground animate-pulse">
              Loading assignments…
            </div>
          ) : assignments.error ? (
            <div className="px-6 py-8 text-sm text-destructive flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>Failed to load: {assignments.error.message}</div>
            </div>
          ) : !assignments.data || assignments.data.items.length === 0 ? (
            <div className="px-6 py-8 text-sm text-muted-foreground text-center">
              No MCP servers assigned to this tenant yet. Pick one from
              the left pane.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Server</TableHead>
                  <TableHead>Trust</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Health</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assignments.data.items.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <div className="font-medium text-sm">{row.displayName}</div>
                      <div className="text-[11px] text-muted-foreground font-mono">{row.slug}</div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={cn(
                          'text-[10px]',
                          row.trustLevel === 'official' && 'border-emerald-600/30 text-emerald-700',
                          row.trustLevel === 'verified' && 'border-blue-600/30 text-blue-700',
                          row.trustLevel === 'community' && 'border-amber-600/30 text-amber-700',
                          row.trustLevel === 'experimental' && 'border-red-600/30 text-red-700',
                        )}
                      >
                        {row.trustLevel}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">v{row.installedVersion}</TableCell>
                    <TableCell>
                      <Badge
                        variant={row.healthStatus === 'healthy' ? 'default' : 'secondary'}
                        className="text-[10px] capitalize"
                      >
                        {row.healthStatus}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Revoke"
                        className="text-destructive hover:bg-destructive/10 h-8 w-8 p-0"
                        onClick={() =>
                          setRevokeTarget({
                            id: row.id,
                            slug: row.slug,
                            displayName: row.displayName,
                          })
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={revokeTarget !== null}
        onOpenChange={(open) => !open && setRevokeTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke MCP server access</AlertDialogTitle>
            <AlertDialogDescription>
              Tenant will lose access to{' '}
              <code className="bg-muted px-1 rounded">{revokeTarget?.slug}</code>{' '}
              ({revokeTarget?.displayName}) immediately. In-flight tool
              calls will fail. The assignment row stays in the database
              with a revoked timestamp for audit; you can re-assign the
              same server later (a new row gets created).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                if (!revokeTarget) return;
                try {
                  await revoke.mutateAsync({
                    id: revokeTarget.id,
                    tenantId,
                    reason: 'Revoked via sysadmin tenant detail',
                  });
                  toast.success(`Revoked ${revokeTarget.slug}`);
                } catch (e) {
                  const msg = e instanceof Error ? e.message : 'Unknown error';
                  toast.error('Revoke failed', { description: msg });
                }
                setRevokeTarget(null);
              }}
            >
              Revoke access
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── v2 placeholders ─────────────────────────────────────────────────────────

function WaveFourPlaceholder({ kind }: { kind: 'workflows' | 'agents' | 'knowledge' }) {
  const config = {
    workflows: {
      title: 'Workflow templates — Wave 4',
      body: 'Tenants clone these into their workspace. Provenance metadata (cloned_from_template_id, cloned_at_version) preserved so tenants are notified of upstream updates.',
    },
    agents: {
      title: 'Agent templates — Wave 4',
      body: 'Agent template adoption + forking. Custom agent wizard imports a template as starting point.',
    },
    knowledge: {
      title: 'Knowledge pack subscriptions — Wave 4',
      body: 'Shared corpora (ANSI Z535, OSHA, ASME) — subscriptions auto-update tenants when sysadmin republishes a pack.',
    },
  }[kind];

  return (
    <Card>
      <CardContent className="py-10 flex flex-col items-center text-center gap-2">
        <Construction className="h-8 w-8 text-muted-foreground" />
        <h3 className="font-semibold">{config.title}</h3>
        <p className="text-sm text-muted-foreground max-w-md">{config.body}</p>
      </CardContent>
    </Card>
  );
}
